import axios from 'axios';
import {ENDPOINT_URL, GET_SWITCH_LIST} from '../../../constants/commonConstants';
import {
	decodeAgentResponse,
	LOOKUP_LIMIT,
	rankLookupCandidates,
	responseRows,
} from '../../core/resolutionUtils';
import {resolveByFields} from '../../core/fieldSearch';
import {readLookupPages} from '../../core/lookupPages';
import {
	getResolvedEntity,
	replaceResolvedEntities,
} from '../../core/resolvedEntityStore';
import {normalizeSwitchReference, switchId} from './reference';

/**
 * Resolve Switch candidates from the authenticated Canvas API.
 *
 * The listing takes the Site in the path rather than as a parameter, which
 * is how Canvas scopes it on the Site Dashboard tab.
 *
 * @param {object} params resolver parameters
 * @param {object} pageContext current Canvas page context
 * @param {object} envelope command cancellation context
 * @return {Promise<{candidates: object[]}>} normalized candidates
 */
export async function resolveSwitches(params = {}, pageContext = {}, envelope = {}) {
	const query = String(params.query || '').trim();
	// `browse` lists what exists instead of matching a name, so a miss can
	// answer with the Switches that are there.
	const browse = params.browse === true;
	if (!query && !browse) throw new Error('lookup_query_required');
	const limit = Math.min(
		Math.max(Number(params.limit) || LOOKUP_LIMIT, 1),
		LOOKUP_LIMIT,
	);
	const scope = params.site_id
		? `${encodeURIComponent(params.site_id)}/`
		: '';
	const url = `${ENDPOINT_URL}${GET_SWITCH_LIST}${scope}` +
		`?limit=${limit}&offset=0` +
		(query ? `&search=${encodeURIComponent(query)}` : '');
	const rows = await readLookupPages({
		preview: browse && !query,
		pageSize: limit,
		rowsFrom: responseRows,
		fetchPage: async (offset) => {
			const {data} = await axios.get(url.replace('&offset=0', `&offset=${offset}`), {
				signal: envelope.signal, timeout: 3000,
			});
			return decodeAgentResponse(data) || {};
		},
	});
	replaceResolvedEntities('switch', rows, switchId);
	return {
		candidates: resolveByFields({
			rows,
			normalizeRow: normalizeSwitchReference,
			query,
			limit,
			searchFields: params.search_fields,
			rank: rankLookupCandidates,
			// A switch is named by what is printed on it as often as by what
			// it was called in Canvas, so all three answer a lookup.
			fallbackValues: (candidate) => [
				candidate.name,
				candidate.serial_no,
			],
			fuzzy: params.fuzzy === true,
		}),
	};
}

/**
 * Return a retained Canvas Switch row.
 * @param {string} entityId serial number
 * @return {object|null} Canvas row
 */
export function getResolvedSwitch(entityId) {
	return getResolvedEntity('switch', entityId);
}
